import { useState, useRef } from "react";
import { Upload, X, Image as ImageIcon, Film, Youtube, Loader2, GripVertical, Star } from "lucide-react";
import { toast } from "sonner";
import {
  uploadImageToCloudinary,
  uploadVideoToCloudinary,
  deleteFromCloudinary,
  parseYouTubeUrl,
  cdnTransform,
  CLOUDINARY_ENABLED,
  VIDEO_MAX_SECONDS,
  IMAGE_MAX_BYTES,
  VIDEO_MAX_BYTES,
} from "../lib/cloudinary";

const MAX_IMAGES = 5;
const TABS = [
  { id: "images", label: "Images", icon: ImageIcon },
  { id: "video-upload", label: "Upload Video", icon: Film },
  { id: "video-youtube", label: "YouTube Link", icon: Youtube },
];

/**
 * Admin Media Manager — managed by the parent gemstone editor.
 *
 * Props:
 *  - images      : Array<{ url, public_id, alt, order }>
 *  - video       : null | { type, url, public_id, thumbnail_url, duration_seconds, youtube_id }
 *  - onChange    : ({ images, video }) => void
 *  - folder      : Cloudinary folder ("opikjewels/gemstones" by default)
 */
const MediaManager = ({ images = [], video = null, onChange, folder = "opikjewels/gemstones" }) => {
  const [tab, setTab] = useState("images");
  const [uploadingImg, setUploadingImg] = useState(false);
  const [uploadingVid, setUploadingVid] = useState(false);
  const [imgProgress, setImgProgress] = useState(0);
  const [vidProgress, setVidProgress] = useState(0);
  const [ytInput, setYtInput] = useState("");
  const [dragIdx, setDragIdx] = useState(null);
  const imgInputRef = useRef(null);
  const vidInputRef = useRef(null);

  // ---------------- IMAGES ----------------
  const handleImageFiles = async (files) => {
    if (uploadingImg) return;
    const arr = Array.from(files || []);
    if (arr.length === 0) return;
    const remaining = MAX_IMAGES - images.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${MAX_IMAGES} images.`);
      return;
    }
    const slice = arr.slice(0, remaining);
    setUploadingImg(true);
    let added = [...images];
    for (const file of slice) {
      try {
        setImgProgress(0);
        const res = await uploadImageToCloudinary(file, {
          folder,
          onProgress: (p) => setImgProgress(p),
        });
        added = [
          ...added,
          {
            url: res.secure_url,
            public_id: res.public_id,
            alt: "",
            order: added.length,
          },
        ];
        onChange({ images: added, video });
        toast.success(`${file.name} uploaded.`);
      } catch (err) {
        toast.error(err.message || `Upload failed for ${file.name}`);
      }
    }
    setUploadingImg(false);
    setImgProgress(0);
    if (imgInputRef.current) imgInputRef.current.value = "";
  };

  const removeImage = async (idx) => {
    const im = images[idx];
    const next = images.filter((_, i) => i !== idx).map((x, i) => ({ ...x, order: i }));
    onChange({ images: next, video });
    // Best-effort delete from Cloudinary — only for real Cloudinary assets (skip base64 fallback)
    if (im.public_id && CLOUDINARY_ENABLED) {
      deleteFromCloudinary(im.public_id, "image").catch(() => {});
    }
  };

  const setAlt = (idx, value) => {
    const next = images.map((x, i) => (i === idx ? { ...x, alt: value } : x));
    onChange({ images: next, video });
  };

  // Drag & drop reorder
  const onDragStart = (i) => setDragIdx(i);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (i) => {
    if (dragIdx === null || dragIdx === i) return;
    const next = [...images];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(i, 0, moved);
    onChange({ images: next.map((x, k) => ({ ...x, order: k })), video });
    setDragIdx(null);
  };

  const makeCover = (idx) => {
    if (idx === 0) return;
    const next = [...images];
    const [moved] = next.splice(idx, 1);
    next.unshift(moved);
    onChange({ images: next.map((x, k) => ({ ...x, order: k })), video });
  };

  // ---------------- VIDEO (UPLOAD) ----------------
  const handleVideoFile = async (file) => {
    if (!file || uploadingVid) return;
    setUploadingVid(true);
    setVidProgress(0);
    try {
      const res = await uploadVideoToCloudinary(file, {
        folder,
        onProgress: (p) => setVidProgress(p),
      });
      const thumb = cdnTransform(res.secure_url, "so_1,w_800,h_800,c_fill,q_auto,f_auto").replace(/\.(mp4|webm|mov)$/i, ".jpg");
      // Replace existing video (if upload type, also delete the old one)
      if (video?.type === "upload" && video.public_id) {
        deleteFromCloudinary(video.public_id, "video").catch(() => {});
      }
      onChange({
        images,
        video: {
          type: "upload",
          url: res.secure_url,
          public_id: res.public_id,
          thumbnail_url: thumb,
          duration_seconds: res.duration,
          youtube_id: null,
        },
      });
      toast.success("Video uploaded.");
    } catch (err) {
      toast.error(err.message || "Video upload failed");
    } finally {
      setUploadingVid(false);
      setVidProgress(0);
      if (vidInputRef.current) vidInputRef.current.value = "";
    }
  };

  // ---------------- VIDEO (YOUTUBE) ----------------
  const handleYoutubeAttach = () => {
    const parsed = parseYouTubeUrl(ytInput);
    if (!parsed) {
      toast.error("Invalid YouTube URL.");
      return;
    }
    // If switching from upload, free the Cloudinary asset
    if (video?.type === "upload" && video.public_id) {
      deleteFromCloudinary(video.public_id, "video").catch(() => {});
    }
    onChange({
      images,
      video: {
        type: "youtube",
        url: parsed.watch_url,
        public_id: null,
        thumbnail_url: parsed.thumbnail_url,
        duration_seconds: null,
        youtube_id: parsed.id,
      },
    });
    setYtInput("");
    toast.success("YouTube video linked.");
  };

  const removeVideo = () => {
    if (video?.type === "upload" && video.public_id) {
      deleteFromCloudinary(video.public_id, "video").catch(() => {});
    }
    onChange({ images, video: null });
  };

  return (
    <div data-testid="media-manager" className="border border-white/15 bg-black/40">
      <div className="flex border-b border-white/15">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`media-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-2 font-accent text-[10px] tracking-[0.24em] uppercase px-4 py-3 border-r border-white/15 last:border-r-0 transition-colors ${
              tab === t.id ? "bg-gold/10 text-gold" : "text-white/50 hover:text-white"
            }`}
          >
            <t.icon size={12} /> {t.label}
          </button>
        ))}
      </div>

      <div className="p-5">
        {tab === "images" && (
          <div data-testid="media-tab-panel-images">
            <p className="text-white/50 text-xs mb-4">
              Upload up to <span className="text-gold">{MAX_IMAGES} images</span>. First image = cover. Drag the handle to reorder.{" "}
              {CLOUDINARY_ENABLED ? (
                <>Max {(IMAGE_MAX_BYTES / 1024 / 1024).toFixed(0)} MB each. Auto-WebP + CDN delivery.</>
              ) : (
                <span className="text-yellow-300/80">CDN offline — images stored inline (4 MB each, JPG/PNG/WebP).</span>
              )}
            </p>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              {images.map((im, i) => (
                <div
                  key={im.public_id || im.url}
                  data-testid={`media-image-${i}`}
                  draggable
                  onDragStart={() => onDragStart(i)}
                  onDragOver={onDragOver}
                  onDrop={() => onDrop(i)}
                  className={`relative border ${i === 0 ? "border-gold" : "border-white/20"} bg-black group`}
                >
                  <img
                    src={cdnTransform(im.url, "w_320,h_320,c_fill,q_auto,f_auto")}
                    alt={im.alt}
                    className="w-full aspect-square object-cover"
                  />
                  {i === 0 && (
                    <span className="absolute top-1.5 left-1.5 bg-gold text-black font-accent text-[9px] tracking-[0.2em] uppercase px-2 py-0.5">
                      Cover
                    </span>
                  )}
                  <div className="absolute top-1.5 right-1.5 flex gap-1">
                    {i !== 0 && (
                      <button
                        type="button"
                        data-testid={`media-make-cover-${i}`}
                        onClick={() => makeCover(i)}
                        title="Make cover"
                        className="w-7 h-7 bg-black/70 text-white/80 hover:text-gold flex items-center justify-center"
                      >
                        <Star size={12} />
                      </button>
                    )}
                    <button
                      type="button"
                      data-testid={`media-remove-image-${i}`}
                      onClick={() => removeImage(i)}
                      title="Remove"
                      className="w-7 h-7 bg-black/70 text-white/80 hover:text-red-400 flex items-center justify-center"
                    >
                      <X size={12} />
                    </button>
                  </div>
                  <div className="absolute bottom-1.5 left-1.5 w-7 h-7 bg-black/70 text-white/60 cursor-grab flex items-center justify-center" title="Drag to reorder">
                    <GripVertical size={12} />
                  </div>
                  <input
                    type="text"
                    placeholder="Alt text (for SEO)"
                    value={im.alt || ""}
                    onChange={(e) => setAlt(i, e.target.value)}
                    data-testid={`media-alt-${i}`}
                    className="w-full bg-black/60 text-white/80 text-xs px-2 py-1.5 border-t border-white/20 focus:outline-none focus:bg-black/80 placeholder:text-white/30"
                  />
                </div>
              ))}

              {images.length < MAX_IMAGES && (
                <button
                  type="button"
                  data-testid="media-add-image-btn"
                  onClick={() => imgInputRef.current?.click()}
                  disabled={uploadingImg}
                  className="aspect-square border border-dashed border-white/30 hover:border-gold text-white/50 hover:text-gold flex flex-col items-center justify-center gap-2 transition-colors disabled:opacity-50"
                >
                  {uploadingImg ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      <span className="font-accent text-[10px] tracking-[0.2em] uppercase">{imgProgress}%</span>
                    </>
                  ) : (
                    <>
                      <Upload size={20} />
                      <span className="font-accent text-[10px] tracking-[0.2em] uppercase">Add Image</span>
                    </>
                  )}
                </button>
              )}
            </div>

            <input
              ref={imgInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              onChange={(e) => handleImageFiles(e.target.files)}
              className="hidden"
              data-testid="media-image-input"
            />
            {images.length === 0 && (
              <p className="text-yellow-500/80 text-xs">⚠ At least 1 image is required to publish this gemstone.</p>
            )}
          </div>
        )}

        {tab === "video-upload" && (
          <div data-testid="media-tab-panel-video-upload">
            {!CLOUDINARY_ENABLED ? (
              <div className="bg-yellow-500/10 border border-yellow-500/30 p-5 text-yellow-200/90 text-sm leading-relaxed">
                <div className="font-accent text-[11px] tracking-[0.22em] uppercase text-yellow-400 mb-2">Video upload temporarily disabled</div>
                <p>
                  Direct video uploads require a configured Cloudinary CDN account. The credentials currently set in
                  <code className="text-yellow-300 mx-1 font-mono text-xs">/app/backend/.env</code>
                  are not accepted by Cloudinary.
                </p>
                <p className="mt-2">
                  In the meantime, please use the <span className="text-gold">YouTube Link</span> tab — it works perfectly for cinematic product videos.
                </p>
              </div>
            ) : (
              <>
                <p className="text-white/50 text-xs mb-4">
                  MP4, MOV or WebM · {VIDEO_MAX_SECONDS}s max · {(VIDEO_MAX_BYTES / 1024 / 1024).toFixed(0)} MB max. Cloudinary auto-compresses for fast delivery.
                </p>
                {video?.type === "upload" ? (
                  <div className="flex items-start gap-4">
                    <video
                      src={video.url}
                      controls
                      muted
                      playsInline
                      className="w-64 aspect-video bg-black border border-gold/40"
                    />
                    <div className="flex-1">
                      <div className="font-accent text-[10px] tracking-[0.2em] uppercase text-gold mb-2">Active Video</div>
                      <div className="text-white/60 text-xs mb-1">
                        Duration: {video.duration_seconds ? `${video.duration_seconds.toFixed(1)}s` : "—"}
                      </div>
                      <div className="text-white/40 text-[10px] break-all mb-3">{video.public_id}</div>
                      <button
                        type="button"
                        data-testid="media-remove-video"
                        onClick={removeVideo}
                        className="border border-white/30 text-white/80 hover:border-red-400 hover:text-red-400 font-accent text-[10px] tracking-[0.2em] uppercase px-4 py-2"
                      >
                        Remove Video
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    data-testid="media-add-video-btn"
                    onClick={() => vidInputRef.current?.click()}
                    disabled={uploadingVid}
                    className="w-full border border-dashed border-white/30 hover:border-gold text-white/50 hover:text-gold flex flex-col items-center justify-center gap-2 py-12 transition-colors disabled:opacity-50"
                  >
                    {uploadingVid ? (
                      <>
                        <Loader2 size={28} className="animate-spin" />
                        <span className="font-accent text-[11px] tracking-[0.24em] uppercase">Uploading {vidProgress}%</span>
                      </>
                    ) : (
                      <>
                        <Film size={28} />
                        <span className="font-accent text-[11px] tracking-[0.24em] uppercase">Select Video</span>
                      </>
                    )}
                  </button>
                )}
                <input
                  ref={vidInputRef}
                  type="file"
                  accept="video/mp4,video/quicktime,video/webm"
                  onChange={(e) => handleVideoFile(e.target.files?.[0])}
                  className="hidden"
                  data-testid="media-video-input"
                />
              </>
            )}
          </div>
        )}

        {tab === "video-youtube" && (
          <div data-testid="media-tab-panel-video-youtube">
            <p className="text-white/50 text-xs mb-4">
              Paste a YouTube watch / share / Shorts URL. We&apos;ll embed it with related-video suggestions disabled.
            </p>
            {video?.type === "youtube" ? (
              <div className="flex items-start gap-4">
                <img
                  src={video.thumbnail_url}
                  alt="YouTube thumbnail"
                  className="w-64 aspect-video object-cover bg-black border border-gold/40"
                />
                <div className="flex-1">
                  <div className="font-accent text-[10px] tracking-[0.2em] uppercase text-gold mb-2">Linked Video</div>
                  <a href={video.url} target="_blank" rel="noreferrer" className="text-white/70 hover:text-gold text-xs break-all block mb-3">
                    {video.url}
                  </a>
                  <button
                    type="button"
                    data-testid="media-remove-youtube"
                    onClick={removeVideo}
                    className="border border-white/30 text-white/80 hover:border-red-400 hover:text-red-400 font-accent text-[10px] tracking-[0.2em] uppercase px-4 py-2"
                  >
                    Remove Link
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  type="url"
                  value={ytInput}
                  onChange={(e) => setYtInput(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                  data-testid="media-youtube-input"
                  className="flex-1 bg-transparent border border-white/20 px-3 py-3 text-white focus:outline-none focus:border-gold"
                />
                <button
                  type="button"
                  data-testid="media-attach-youtube"
                  onClick={handleYoutubeAttach}
                  disabled={!ytInput.trim()}
                  className="bg-gold text-black font-accent uppercase tracking-[0.24em] text-xs px-5 py-3 hover:bg-ivory disabled:opacity-50"
                >
                  Attach
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaManager;
